import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { ErrorNotification } from "@via/utils/interfaces.tsx";
import { AxiosError } from "axios";
import {
  ConnectionResult,
  CreateMcpServerRequestBody,
  ListMcpServersResponse,
  McpServerDetail,
  McpTool,
  TestConnectionRequestBody,
  TestConnectionResult,
  UpdateMcpServerRequestBody,
} from "common";

// ── Filter helpers ─────────────────────────────────────────────────────────

const buildFilterString = (
  filters: Array<{ id: string; value: unknown }>
): string => {
  const filterParts: string[] = [];

  filters.forEach(filter => {
    if (
      filter.value === "" ||
      filter.value === null ||
      filter.value === undefined
    ) {
      return;
    }

    const filterMap: Record<string, { field: string; operator: string }> = {
      name: { field: "name", operator: "like" },
      transportType: { field: "transportType", operator: "eq" },
      authType: { field: "authType", operator: "eq" },
      isEnabled: { field: "isEnabled", operator: "eq" },
      connectionStatus: { field: "connectionStatus", operator: "eq" },
    };

    const filterConfig = filterMap[filter.id];
    if (filterConfig) {
      const filterValue =
        filterConfig.operator === "like" ? `%${filter.value}%` : filter.value;
      filterParts.push(
        `${filterConfig.field}:${filterConfig.operator}=${filterValue}`
      );
    }
  });

  return filterParts.join(",");
};

// ── List ────────────────────────────────────────────────────────────────────

export const fetchMcpServersList = async (
  take: number,
  skip: number,
  sort: Array<{ id: string; desc: boolean }>,
  filters: Array<{ id: string; value: unknown }>
): Promise<ListMcpServersResponse> => {
  try {
    const baseUrl = "/api/v1/cortex/mcp-server/list";
    const queryParams = new URLSearchParams({
      take: `${take}`,
      skip: `${skip}`,
    });

    if (sort && sort.length > 0) {
      queryParams.append("sort", sort[0].id);
      queryParams.append("order", sort[0].desc ? "desc" : "asc");
    }
    if (filters && filters.length > 0) {
      const filterQuery = buildFilterString(filters);
      if (filterQuery) {
        queryParams.append("filter", filterQuery);
      }
    }

    const url = `${baseUrl}?${queryParams.toString()}`;
    const res = await axios.get<ListMcpServersResponse>(url);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useMcpServersList = (
  page: number = 0,
  pageSize: number = 10,
  sort: Array<{ id: string; desc: boolean }> = [],
  filters: Array<{ id: string; value: unknown }> = [],
  enabled: boolean = true
): UseQueryResult<ListMcpServersResponse, Error> => {
  const skip = page * pageSize;
  const take = pageSize;

  return useQuery({
    queryKey: ["/api/v1/cortex/mcp-server/list", take, skip, sort, filters],
    queryFn: () => fetchMcpServersList(take, skip, sort, filters),
    retry: false,
    enabled,
    refetchInterval: 30_000,
  });
};

// ── Get by ID ───────────────────────────────────────────────────────────────

export const fetchMcpServerById = async (
  id: number
): Promise<McpServerDetail> => {
  try {
    const res = await axios.get<McpServerDetail>(
      `/api/v1/cortex/mcp-server/${id}`
    );
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useMcpServerById = (
  id: number | null
): UseQueryResult<McpServerDetail, Error> => {
  return useQuery({
    queryKey: ["/api/v1/cortex/mcp-server", id],
    queryFn: () => fetchMcpServerById(id as number),
    retry: false,
    enabled: !!id,
  });
};

// ── Create ──────────────────────────────────────────────────────────────────

export const useCreateMcpServer = (): UseMutationResult<
  McpServerDetail,
  AxiosError | ErrorNotification,
  CreateMcpServerRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (payload: CreateMcpServerRequestBody) => {
      const res = await axios.post("/api/v1/cortex/mcp-server", payload);
      return res.data;
    },
    onSuccess: (data): void => {
      setNotification({
        id: "mcp-server-created",
        message: `"${data.name}" Successfully Created`,
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/cortex/mcp-server/list"],
      });
    },
    onError: (error: ErrorNotification, variables) => {
      setNotification({
        id: "mcp-server-creation-failed",
        message: `"${variables.name}" Creation Failed`,
        details:
          error.response?.data?.message ?? "MCP Server could not be created",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

// ── Update ──────────────────────────────────────────────────────────────────

export const useUpdateMcpServer = (): UseMutationResult<
  McpServerDetail,
  AxiosError | ErrorNotification,
  { id: number; payload: UpdateMcpServerRequestBody }
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (params: {
      id: number;
      payload: UpdateMcpServerRequestBody;
    }) => {
      const res = await axios.put(
        `/api/v1/cortex/mcp-server/${params.id}`,
        params.payload
      );
      return res.data;
    },
    onSuccess: (data): void => {
      setNotification({
        id: "mcp-server-updated",
        message: `"${data.name}" Successfully Updated`,
        details: "Changes have been saved",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/cortex/mcp-server/list"],
      });
    },
    onError: (error: ErrorNotification, variables) => {
      setNotification({
        id: "mcp-server-update-failed",
        message: `"${variables.payload.name ?? "MCP Server"}" Update Failed`,
        details:
          error.response?.data?.message ?? "MCP Server could not be updated",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

// ── Delete ──────────────────────────────────────────────────────────────────

export const useDeleteMcpServer = (): UseMutationResult<
  void,
  AxiosError | ErrorNotification,
  { id: number; name: string }
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async ({ id }: { id: number; name: string }): Promise<void> => {
      await axios.delete(`/api/v1/cortex/mcp-server/${id}`);
    },
    onSuccess: (_, variables): void => {
      setNotification({
        id: "mcp-server-deleted",
        message: `"${variables.name}" Successfully Deleted`,
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/cortex/mcp-server/list"],
      });
    },
    onError: (error: ErrorNotification, variables) => {
      setNotification({
        id: "mcp-server-deletion-failed",
        message: `"${variables.name}" Deletion Failed`,
        details: error.response?.data?.message ?? "Failed to delete MCP Server",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

// ── Toggle Status ───────────────────────────────────────────────────────────

export const useToggleMcpServerStatus = (): UseMutationResult<
  McpServerDetail,
  AxiosError | ErrorNotification,
  { id: number; name: string; isEnabled: boolean }
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (params: {
      id: number;
      name: string;
      isEnabled: boolean;
    }) => {
      const res = await axios.patch(
        `/api/v1/cortex/mcp-server/${params.id}/status`,
        { isEnabled: params.isEnabled }
      );
      return res.data;
    },
    onSuccess: (_data, variables): void => {
      setNotification({
        id: "mcp-server-status-toggled",
        message: `"${variables.name}" Successfully ${variables.isEnabled ? "Enabled" : "Disabled"}`,
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/cortex/mcp-server/list"],
      });
    },
    onError: (error: ErrorNotification, variables) => {
      setNotification({
        id: "mcp-server-status-toggle-failed",
        message: `"${variables.name}" Status Update Failed`,
        details:
          error.response?.data?.message ?? "Failed to update MCP Server status",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

// ── Test Connection ─────────────────────────────────────────────────────────

export const useTestMcpConnection = (): UseMutationResult<
  TestConnectionResult,
  AxiosError | ErrorNotification,
  TestConnectionRequestBody
> => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (payload: TestConnectionRequestBody) => {
      const res = await axios.post(
        "/api/v1/cortex/mcp-server/test-connection",
        payload
      );
      return res.data;
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "mcp-test-connection-failed",
        message: "Test Connection Failed",
        details: error.response?.data?.message ?? "Could not test connection",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

// ── Connect / Disconnect ────────────────────────────────────────────────────

export const useConnectMcpServer = (): UseMutationResult<
  ConnectionResult,
  AxiosError | ErrorNotification,
  { id: number; name: string }
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async ({ id }: { id: number; name: string }) => {
      const res = await axios.post(`/api/v1/cortex/mcp-server/${id}/connect`);
      return res.data;
    },
    onSuccess: (data, variables): void => {
      if (data.success) {
        setNotification({
          id: "mcp-server-connected",
          message: `"${variables.name}" Connected`,
          details: data.toolCount
            ? `Discovered ${data.toolCount} tool${data.toolCount === 1 ? "" : "s"}`
            : undefined,
          variant: NOTIFICATION.SUCCESS,
        });
      } else {
        setNotification({
          id: "mcp-server-connection-failed",
          message: `"${variables.name}" Connection Failed`,
          details: data.error ?? "Could not connect to the MCP server",
          variant: NOTIFICATION.ERROR,
        });
      }
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/cortex/mcp-server/list"],
      });
    },
    onError: (error: ErrorNotification, variables) => {
      setNotification({
        id: "mcp-server-connection-error",
        message: `"${variables.name}" Connection Error`,
        details:
          error.response?.data?.message ?? "Failed to connect to MCP server",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useDisconnectMcpServer = (): UseMutationResult<
  void,
  AxiosError | ErrorNotification,
  { id: number; name: string }
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async ({ id }: { id: number; name: string }): Promise<void> => {
      await axios.post(`/api/v1/cortex/mcp-server/${id}/disconnect`);
    },
    onSuccess: (_, variables): void => {
      setNotification({
        id: "mcp-server-disconnected",
        message: `"${variables.name}" Disconnected`,
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/cortex/mcp-server/list"],
      });
    },
    onError: (error: ErrorNotification, variables) => {
      setNotification({
        id: "mcp-server-disconnect-failed",
        message: `"${variables.name}" Disconnect Failed`,
        details:
          error.response?.data?.message ??
          "Failed to disconnect from MCP server",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

// ── Tools ───────────────────────────────────────────────────────────────────

export const useMcpServerTools = (
  id: number | null
): UseQueryResult<McpTool[], Error> => {
  return useQuery({
    queryKey: ["/api/v1/cortex/mcp-server/tools", id],
    queryFn: async () => {
      const res = await axios.get<McpTool[]>(
        `/api/v1/cortex/mcp-server/${id}/tools`
      );
      return res.data;
    },
    retry: false,
    enabled: !!id,
  });
};

export const useRefreshMcpServerTools = (): UseMutationResult<
  McpTool[],
  AxiosError | ErrorNotification,
  { id: number; name: string }
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async ({ id }: { id: number; name: string }) => {
      const res = await axios.post(
        `/api/v1/cortex/mcp-server/${id}/tools/refresh`
      );
      return res.data;
    },
    onSuccess: (data, variables): void => {
      setNotification({
        id: "mcp-tools-refreshed",
        message: `"${variables.name}" Tools Refreshed`,
        details: `Found ${data.length} tool${data.length === 1 ? "" : "s"}`,
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/cortex/mcp-server/tools", variables.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/cortex/mcp-server/list"],
      });
    },
    onError: (error: ErrorNotification, variables) => {
      setNotification({
        id: "mcp-tools-refresh-failed",
        message: `"${variables.name}" Tools Refresh Failed`,
        details: error.response?.data?.message ?? "Failed to refresh tools",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};
